/**
 * Projektbalken des Jahres. Uebernommen aus der alten Ansicht, nur die Quelle
 * hat gewechselt: `model.projects` statt der eigenen Projektschleife.
 */
import React, { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { CockpitModel, ProjectRow } from '../../services/cockpitTypes';
import { MO, pctOf, statusChip } from './cockpitUi';
import { exportGanttPdf, type PdfContext } from './cockpitPdf';

type GanttSort = 'responsible' | 'duration' | 'endDate' | 'status' | 'value';

const SORTS: { key: GanttSort; label: string }[] = [
    { key: 'responsible', label: 'Verantwortlich' },
    { key: 'duration', label: 'Laufzeit' },
    { key: 'endDate', label: 'Endtermin' },
    { key: 'status', label: 'Status' },
    { key: 'value', label: 'Vertragswert' },
];

const ROW_H = 28;
const HEADER_H = 50;
const LABEL_W = 220;
const CHART_W = 900;

const STATUS_COLOR: Record<string, string> = { active: '#10b981', planning: '#60a5fa', on_hold: '#f59e0b' };

const days = (from: string | null, to: string | null): number | null =>
    from && to ? Math.ceil((Date.parse(to) - Date.parse(from)) / 864e5) : null;

export interface CockpitGanttProps {
    model: CockpitModel;
    numbers: Record<string, string>;
    pdfContext: PdfContext;
    onSelect: (project: ProjectRow) => void;
}

export const CockpitGantt: React.FC<CockpitGanttProps> = ({ model, numbers, pdfContext, onSelect }) => {
    const [sort, setSort] = useState<GanttSort>('responsible');
    const [exporting, setExporting] = useState(false);

    const year = model.period.year;
    const yearStart = Date.parse(`${year}-01-01`);
    const yearSpan = Date.parse(`${year}-12-31`) - yearStart;

    const projects = useMemo(() => {
        const list = model.projects.filter((p) => p.startDate && p.status !== 'finished' && p.status !== 'completed');
        return [...list].sort((a, b) => {
            if (sort === 'responsible') return a.responsibleName.localeCompare(b.responsibleName) || a.name.localeCompare(b.name);
            if (sort === 'endDate') return (a.daysRemaining ?? 99999) - (b.daysRemaining ?? 99999);
            if (sort === 'status') {
                const order: Record<string, number> = { active: 0, planning: 1, on_hold: 2 };
                return (order[a.status] ?? 3) - (order[b.status] ?? 3);
            }
            if (sort === 'value') return b.contractCents - a.contractCents;
            return (days(a.startDate, a.endDate) ?? 9999) - (days(b.startDate, b.endDate) ?? 9999);
        });
    }, [model.projects, sort]);

    type Row = { kind: 'group'; label: string } | { kind: 'project'; project: ProjectRow };
    const rows = useMemo<Row[]>(() => {
        if (sort !== 'responsible') return projects.map((project) => ({ kind: 'project', project }));
        const out: Row[] = [];
        let current = '';
        projects.forEach((project) => {
            if (project.responsibleName !== current) {
                current = project.responsibleName;
                out.push({ kind: 'group', label: current });
            }
            out.push({ kind: 'project', project });
        });
        return out;
    }, [projects, sort]);

    const svgH = HEADER_H + rows.length * ROW_H + 20;
    const xOf = (iso: string): number => {
        const frac = Math.min(1, Math.max(0, (Date.parse(iso) - yearStart) / yearSpan));
        return LABEL_W + frac * (CHART_W - LABEL_W);
    };
    const todayX = LABEL_W + Math.min(1, Math.max(0, (Date.now() - yearStart) / yearSpan)) * (CHART_W - LABEL_W);

    const runExport = async () => {
        setExporting(true);
        try {
            await exportGanttPdf({
                rows: projects, numbers, year,
                sortLabel: SORTS.find((s) => s.key === sort)?.label ?? '',
                ctx: pdfContext,
            });
        } catch (e) {
            console.error(e);
            alert('PDF-Export fehlgeschlagen.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-glass-view="cockpit" className="space-y-4">
            <div data-glass="auf-fenster" className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">Sortierung:</span>
                    <div data-glass="umschalter" className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                        {SORTS.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => setSort(s.key)}
                                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold ${sort === s.key ? 'bg-white text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{projects.length} Projekte</span>
                    <button
                        onClick={runExport}
                        disabled={exporting}
                        className="h-8 px-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-500 hover:text-blue-600 hover:border-blue-200 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-50"
                    >
                        {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {exporting ? '…' : 'Gantt PDF'}
                    </button>
                </div>
            </div>

            <div data-glass="auf-fenster" className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm bg-emerald-500" />Aktiv</span>
                <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm bg-blue-400" />Planung</span>
                <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm bg-amber-400" />Ruht</span>
                <span className="flex items-center gap-1"><span className="w-px h-3 bg-red-500" />Heute</span>
            </div>

            <div data-glass="diagramm" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <svg viewBox={`0 0 ${CHART_W} ${svgH}`} className="w-full min-w-[900px] h-auto select-none" style={{ minHeight: `${Math.min(svgH, 800)}px` }}>
                        {MO.map((m, i) => {
                            const x = LABEL_W + (i / 12) * (CHART_W - LABEL_W);
                            const mW = (CHART_W - LABEL_W) / 12;
                            return (
                                <g key={m}>
                                    <rect x={x} y={0} width={mW} height={HEADER_H} fill={i % 2 === 0 ? '#fafbfc' : '#f1f5f9'} />
                                    <text x={x + mW / 2} y={22} textAnchor="middle" className="text-[11px] font-semibold fill-slate-500">{m}</text>
                                    <text x={x + mW / 2} y={38} textAnchor="middle" className="text-[11px] fill-slate-400">{String(year).slice(2)}</text>
                                    <line x1={x} y1={HEADER_H} x2={x} y2={svgH} stroke="#f1f5f9" strokeWidth="1" />
                                </g>
                            );
                        })}

                        {rows.map((row, i) => {
                            const y = HEADER_H + i * ROW_H;
                            if (row.kind === 'group') {
                                return (
                                    <g key={`g-${row.label}-${i}`}>
                                        <rect x={0} y={y} width={CHART_W} height={ROW_H} fill="#f8fafc" />
                                        <text x={12} y={y + ROW_H / 2 + 4} className="text-[11px] font-semibold fill-slate-700">{row.label}</text>
                                        <line x1={0} y1={y + ROW_H} x2={CHART_W} y2={y + ROW_H} stroke="#e2e8f0" strokeWidth="0.5" />
                                    </g>
                                );
                            }
                            const p = row.project;
                            const barStart = p.startDate ? xOf(p.startDate) : LABEL_W;
                            const barEnd = p.endDate ? xOf(p.endDate) : CHART_W - 10;
                            const barW = Math.max(8, barEnd - barStart);
                            const color = STATUS_COLOR[p.status] ?? '#94a3b8';
                            const progress = pctOf(p.performedLifetimeCents, p.contractCents);
                            const duration = days(p.startDate, p.endDate);
                            return (
                                <g key={p.id} className="cursor-pointer" onClick={() => onSelect(p)}>
                                    <rect x={0} y={y} width={CHART_W} height={ROW_H} fill={i % 2 === 0 ? '#ffffff' : '#fafbfc'} />
                                    <line x1={0} y1={y + ROW_H} x2={CHART_W} y2={y + ROW_H} stroke="#f1f5f9" strokeWidth="0.5" />
                                    <text x={8} y={y + 12} style={{ fontSize: '11px' }} className="font-semibold fill-slate-700">
                                        {p.name.length > 26 ? `${p.name.substring(0, 26)}…` : p.name}
                                    </text>
                                    <text x={8} y={y + 23} style={{ fontSize: '11px' }} className="fill-slate-400">
                                        #{numbers[p.id] ?? '—'} · {p.responsibleName}{duration !== null ? ` · ${duration}d` : ''}
                                    </text>
                                    <rect x={barStart} y={y + 6} width={barW} height={16} rx="3" fill={color} opacity="0.15" />
                                    <rect x={barStart} y={y + 6} width={barW * (Math.min(100, progress) / 100)} height={16} rx="3" fill={color} opacity="0.6" />
                                    <rect x={barStart} y={y + 6} width={barW} height={16} rx="3" fill="none" stroke={color} strokeWidth="1" opacity="0.4" />
                                    {barW > 44 && (
                                        <text x={barStart + barW / 2} y={y + 18} textAnchor="middle" style={{ fontSize: '11px' }} className="fill-white font-semibold">
                                            {progress}%
                                        </text>
                                    )}
                                    {p.daysRemaining !== null && p.daysRemaining <= 60 && p.daysRemaining > 0 && (
                                        <circle cx={barEnd} cy={y + 14} r="4" fill={p.daysRemaining <= 30 ? '#ef4444' : '#f59e0b'} stroke="white" strokeWidth="1" />
                                    )}
                                    <title>{`${p.name} — ${statusChip(p.status).label}`}</title>
                                </g>
                            );
                        })}

                        <line x1={LABEL_W} y1={0} x2={LABEL_W} y2={svgH} stroke="#e2e8f0" strokeWidth="1" />
                        <line x1={todayX} y1={0} x2={todayX} y2={svgH} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.8" />
                        <rect x={todayX - 17} y={2} width="34" height="15" rx="4" fill="#ef4444" />
                        <text x={todayX} y={13} textAnchor="middle" className="text-[11px] font-semibold fill-white">Heute</text>
                    </svg>
                </div>
            </div>
        </div>
    );
};
